// The ONE modal the onboarding area uses (Module 02 §4, a11y).
//
// Before this existed the wizard had four different confirmation surfaces —
// `role="dialog"` divs with no focus management (go-live, waive, plant deletion),
// a `role="group"` doing the same job (pack switch), and two `window.confirm`
// calls (import discard). None moved focus, none trapped Tab, none restored
// focus, none closed on Escape, and the in-page ones rendered ABOVE the control
// that opened them — so pressing "Install" on a pack card low in the grid looked
// like nothing happened, because the confirmation opened off-screen.
//
// This component fixes all of that in one place:
//  * a scrim that covers the page and closes on click (never a destructive path
//    — closing is always the safe outcome);
//  * focus moves INTO the dialog on open (the primary control, so a keyboard
//    user lands on the decision), is trapped inside it, and is restored to the
//    element that opened it on close;
//  * Escape closes;
//  * `aria-modal` + `aria-labelledby`/`aria-describedby` so a screen reader
//    announces what is being confirmed rather than leaving the user behind it;
//  * `role="alertdialog"` for destructive confirmations, which is what going
//    live, archiving plant nodes and discarding an import all are.
//
// Every confirmation in the area goes through it, so a walkthrough can drive
// them all the same way (`data-testid="wizard-dialog"`) — `window.confirm`
// could not be driven at all.

import { useEffect, useId, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface WizardDialogProps {
  /** Dialog heading — the question being asked. */
  title: string;
  /** Consequences, list of what will change, form fields — whatever the caller
   * needs the user to read BEFORE deciding. */
  children: ReactNode;
  /** Label of the confirming action. Omit for an information-only dialog. */
  confirmLabel?: string | undefined;
  onConfirm?: (() => void) | undefined;
  cancelLabel?: string | undefined;
  onCancel: () => void;
  /** True for irreversible actions: `alertdialog` + the danger styling. */
  destructive?: boolean;
  /** Disable the confirm control while the mutation is in flight. */
  busy?: boolean;
  /** Distinguishes dialogs in tests/walkthroughs. */
  testId?: string;
}

export function WizardDialog({
  title,
  children,
  confirmLabel,
  onConfirm,
  cancelLabel,
  onCancel,
  destructive = false,
  busy = false,
  testId,
}: WizardDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const bodyId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const primary = useRef<HTMLButtonElement>(null);
  // Captured on mount, restored on unmount: the control the user pressed to get
  // here is where their focus belongs when the dialog goes away.
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // The primary control when there is one (the decision), else the panel
    // itself so a screen reader reads the dialog rather than the page behind it.
    (primary.current ?? panel.current)?.focus();
    return () => {
      opener.current?.focus();
    };
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCancel();
        return;
      }
      if (event.key !== "Tab" || panel.current === null) return;
      const focusable = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (focusable.length === 0) {
        event.preventDefault();
        panel.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [onCancel]);

  return (
    <div className="wizard-dialog-scrim" data-testid="wizard-dialog-scrim" onClick={onCancel}>
      <div
        ref={panel}
        className={`wizard-dialog${destructive ? " is-destructive" : ""}`}
        role={destructive ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        data-testid={testId ?? "wizard-dialog"}
        onClick={(event) => {
          // Clicks inside the panel must not reach the scrim's close handler.
          event.stopPropagation();
        }}
      >
        <h3 id={titleId} className="wizard-dialog-title">
          {title}
        </h3>
        <div id={bodyId} className="wizard-dialog-body">
          {children}
        </div>
        <div className="wizard-dialog-actions">
          {confirmLabel !== undefined && onConfirm !== undefined ? (
            <button
              ref={primary}
              type="button"
              className={destructive ? "ds-btn ds-btn-danger" : "ds-btn ds-btn-primary"}
              disabled={busy}
              onClick={onConfirm}
            >
              {confirmLabel}
            </button>
          ) : null}
          <Button disabled={busy} onClick={onCancel}>
            {cancelLabel ?? t("onboarding.dialog.cancel")}
          </Button>
        </div>
      </div>
    </div>
  );
}
